import React from 'react';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';

import { SERIF_FONT } from './proseStyle';

type Props = {
  /** 記事タイトル（frontmatter.title を流用）。中央大見出しに使う */
  title: string;
  /** 副題（frontmatter.subtitle） */
  subtitle?: string;
  /** パンくずの親ラベル（frontmatter.breadcrumb）。指定すると「親 > title」を紙面の上に表示 */
  breadcrumb?: string;
  /** 記事本文（章・段落などの読み物ブロック） */
  children: React.ReactNode;
};

/**
 * 読み物（明朝）記事のラッパ。devdocs/thoughts-ai のような「新書を一冊読む体験」を再現する。
 * 生成りの紙面・明朝体・中央寄せタイトルで、装飾を削ぎ落として本文に集中させる。
 *
 * タイトル・副題・パンくずは frontmatter（title / subtitle / breadcrumb）から受け取る。
 * frontmatter に `prose: true` を指定した記事で KnzViewer が本文をこのラッパで包む。
 *
 * 紙面デザインとして意図的に色を固定する（読み物の雰囲気のため。tech_design のトークン優先の例外）。
 */
export const ProseArticle: React.FC<Props> = ({ title, subtitle, breadcrumb, children }) => {
  return (
    <Box sx={{ pb: 2 }}>
      {breadcrumb && (
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
            <Typography variant="body2" color="text.secondary">
              {breadcrumb}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {title}
            </Typography>
          </Breadcrumbs>
        </Box>
      )}

      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          px: 2,
          pt: 10,
          pb: 15,
          fontFamily: SERIF_FONT,
          color: '#1A1A1A',
          bgcolor: '#FAFAF7',
          borderRadius: 2,
          boxShadow: '0 0 40px rgba(0,0,0,0.04)',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: 24,
            fontWeight: 700,
            fontFamily: 'inherit',
            textAlign: 'center',
            lineHeight: 1.6,
            mb: subtitle ? 2 : 4,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ fontSize: 14, fontFamily: 'inherit', textAlign: 'center', color: '#555', mb: 4 }}
          >
            {subtitle}
          </Typography>
        )}
        <Box sx={{ borderBottom: '1px solid #D0CCC5', mb: 7.5 }} />
        {children}
      </Box>
    </Box>
  );
};

export default ProseArticle;
